import React, { useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { JsonBlock, Mono } from '../ai/replay/shared';

// The "Events (N)" title and the expand/collapse-all toggle share one line above
// the timeline box (matching the "Changes" section header in TraceDetail).
const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0 0.75rem;
`;

const Title = styled.h3`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  margin: 0;
`;

// Plain text button — override the global teal <button> styling.
const ToggleAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  border-radius: 0;
  padding: 0;
  height: auto;
  color: ${colors.primary};
  font-size: 0.75rem;
  font-weight: ${weights.medium};
  cursor: pointer;

  &:hover {
    color: ${colors.accent};
    background: none;
  }
`;

const Section = styled.div`
  background: ${colors.white};
  border: solid 1px ${colors.grey20};
  border-radius: 0.375rem;
  padding: 1rem;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const Event = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 0;

  &:not(:last-child) {
    border-bottom: solid 1px ${colors.grey10};
  }
`;

const Dot = styled.div`
  flex-shrink: 0;
  width: 0.625rem;
  height: 0.625rem;
  margin-top: 0.25rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Line = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

// Always-present caret column so an event with no payload (no chevron) keeps its
// type label aligned with the expandable ones rather than shifting left.
const CaretSlot = styled.span`
  flex-shrink: 0;
  width: 0.75rem;
  display: inline-flex;
  justify-content: center;
  font-size: 0.6875rem;
  color: ${colors.grey40};
`;

const Type = styled.span`
  font-size: 0.8125rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  text-transform: capitalize;
`;

const Phase = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $phase }) => ($phase === 'before' ? colors.warning : colors.success)};
`;

const Entity = styled.span`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const Outcome = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $outcome }) => ($outcome === 'failed' ? colors.danger : colors.success)};
`;

const Time = styled.span`
  margin-left: auto;
  font-size: 0.6875rem;
  color: ${colors.grey40};
`;

const Data = styled(JsonBlock)`
  margin-top: 0.5rem;
`;

const EVENT_COLORS = {
  open: colors.primary,
  payload: colors.accent,
  result: colors.accent,
  check: colors.grey40,
  snapshot: colors.grey40,
  notification: colors.accent,
  succeed: colors.success,
  fail: colors.danger,
};

function hasData(eventData) {
  return eventData != null && (typeof eventData !== 'object' || Object.keys(eventData).length > 0);
}

export default function TraceTimeline({ events }) {
  // Events start fully collapsed; clicking one (or Expand all) reveals its payload.
  // Only events that carry data are expandable.
  const [expanded, setExpanded] = useState(() => new Set());
  const dataIndexes = events.map((event, index) => (hasData(event.eventData) ? index : -1)).filter((i) => i >= 0);
  const allExpanded = dataIndexes.length > 0 && dataIndexes.every((i) => expanded.has(i));

  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(dataIndexes));
  const toggle = (index) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <>
      <Header>
        <Title>Events ({events.length})</Title>
        {dataIndexes.length > 0 && (
          <ToggleAll type="button" onClick={toggleAll}>
            <Icon icon={allExpanded ? 'angle-up' : 'angle-down'} />
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </ToggleAll>
        )}
      </Header>
      <Section>
        <Timeline>
          {events.map((event, index) => {
            const expandable = hasData(event.eventData);
            const isOpen = expanded.has(index);
            return (
              <Event key={index}>
                <Dot $color={EVENT_COLORS[event.eventType] || colors.grey40} />
                <Body>
                  <Line $clickable={expandable} onClick={expandable ? () => toggle(index) : undefined}>
                    <CaretSlot>{expandable && <Icon icon={isOpen ? 'angle-down' : 'angle-right'} />}</CaretSlot>
                    <Type>{event.eventType}</Type>
                    {event.eventType === 'snapshot' && (
                      <>
                        <Phase $phase={event.phase}>{event.phase}</Phase>
                        <Entity>
                          {event.entity} <Mono>{event.entityId}</Mono>
                        </Entity>
                      </>
                    )}
                    {event.eventType === 'check' && (
                      <>
                        <Entity>{event.stage}</Entity>
                        <Outcome $outcome={event.outcome}>{event.outcome}</Outcome>
                      </>
                    )}
                    {event.eventType === 'notification' && (
                      <>
                        <Entity>
                          {event.channel} <Mono>{event.recipient}</Mono>
                        </Entity>
                        <Outcome $outcome={event.delivered ? 'delivered' : 'failed'}>
                          {event.delivered ? 'delivered' : 'failed'}
                        </Outcome>
                      </>
                    )}
                    <Time title={event.timestamp}>{moment(event.timestamp).format('MMM D, h:mm:ss.SSS a')}</Time>
                  </Line>
                  {expandable && isOpen && (
                    <Data>
                      {event.eventType === 'log' && typeof event.eventData === 'string'
                        ? event.eventData
                        : JSON.stringify(event.eventData, null, 2)}
                    </Data>
                  )}
                </Body>
              </Event>
            );
          })}
        </Timeline>
      </Section>
    </>
  );
}
